"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Loader2,
  Pencil,
  Plus,
  Star,
  Trash2,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { faNum, faToman } from "@/lib/format";
import type { Plan } from "./types";

type Props = { onRefresh: () => void };

type FormState = {
  name: string;
  description: string;
  priceToman: string;
  durationDays: string;
  trafficGB: string;
  popular: boolean;
  active: boolean;
  sortOrder: string;
};

const EMPTY_FORM: FormState = {
  name: "",
  description: "",
  priceToman: "",
  durationDays: "30",
  trafficGB: "0",
  popular: false,
  active: true,
  sortOrder: "0",
};

export default function PlansManager({ onRefresh }: Props) {
  const [plans, setPlans] = React.useState<Plan[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Plan | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = React.useState(false);

  const [deleteTarget, setDeleteTarget] = React.useState<Plan | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [togglingId, setTogglingId] = React.useState<string | null>(null);

  const loadPlans = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/plans", { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت طرح‌ها");
      }
      setPlans(data.plans ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت طرح‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  }

  function openEdit(p: Plan) {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description ?? "",
      priceToman: String(p.priceToman),
      durationDays: String(p.durationDays),
      trafficGB: String(p.trafficGB),
      popular: p.popular,
      active: p.active,
      sortOrder: String(p.sortOrder),
    });
    setFormOpen(true);
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("نام طرح را وارد کنید.");
      return;
    }
    const price = Number(form.priceToman);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("مبلغ را به عدد وارد کنید.");
      return;
    }
    const days = Number(form.durationDays);
    if (!Number.isFinite(days) || days <= 0) {
      toast.error("مدت طرح (روز) را به عدد صحیح وارد کنید.");
      return;
    }
    const traffic = Number(form.trafficGB);
    if (!Number.isFinite(traffic) || traffic < 0) {
      toast.error("حجم ترافیک را به عدد وارد کنید.");
      return;
    }
    const sort = Number(form.sortOrder) || 0;

    setSubmitting(true);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        priceToman: price,
        durationDays: days,
        trafficGB: traffic,
        popular: form.popular,
        active: form.active,
        sortOrder: sort,
      };
      let res: Response;
      if (editing) {
        res = await fetch("/api/admin/plans", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: editing.id, ...body }),
        });
      } else {
        res = await fetch("/api/admin/plans", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در ذخیره طرح");
      }
      toast.success(editing ? "طرح ویرایش شد." : "طرح جدید ساخته شد.");
      setFormOpen(false);
      loadPlans();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در ذخیره طرح";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(p: Plan, next: boolean) {
    setTogglingId(p.id);
    try {
      const res = await fetch("/api/admin/plans", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: p.id, active: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در تغییر وضعیت طرح");
      toast.success(next ? "طرح فعال شد." : "طرح غیرفعال شد.");
      loadPlans();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در تغییر وضعیت طرح";
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `/api/admin/plans?id=${encodeURIComponent(deleteTarget.id)}`,
        { method: "DELETE" },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در حذف طرح");
      toast.success("طرح حذف شد.");
      setDeleteTarget(null);
      loadPlans();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در حذف طرح";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="border-b">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">مدیریت طرح‌ها</CardTitle>
          <Button className="bg-primary hover:bg-primary/90" onClick={openCreate}>
            <Plus className="size-4" />
            افزودن طرح
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-destructive">{error}</div>
        ) : plans.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            هنوز طرحی تعریف نشده است.
          </div>
        ) : (
          <div className="max-h-[28rem] overflow-y-auto scrollbar-thin">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead className="pr-4">نام طرح</TableHead>
                  <TableHead>مدت</TableHead>
                  <TableHead>حجم</TableHead>
                  <TableHead>مبلغ</TableHead>
                  <TableHead>محبوب</TableHead>
                  <TableHead>فعال</TableHead>
                  <TableHead className="text-center">عملیات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="pr-4 font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm">
                      {faNum(p.durationDays)} روز
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.trafficGB === 0
                        ? "نامحدود"
                        : `${faNum(p.trafficGB)} گیگ`}
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-primary">
                      {faToman(p.priceToman)}
                    </TableCell>
                    <TableCell>
                      {p.popular ? (
                        <Badge className="bg-amber-500/15 text-amber-600 hover:bg-amber-500/20 dark:text-amber-400">
                          <Star className="size-3" />
                          محبوب
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={p.active}
                        onCheckedChange={(v) => toggleActive(p, v)}
                        disabled={togglingId === p.id}
                        aria-label="فعال/غیرفعال"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="ویرایش"
                          onClick={() => openEdit(p)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="حذف"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTarget(p)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Create / Edit form dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "ویرایش طرح" : "افزودن طرح جدید"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "اطلاعات طرح را ویرایش و ذخیره کنید."
                : "یک طرح اشتراک جدید برای فروش تعریف کنید."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="plan-name">نام طرح *</Label>
              <Input
                id="plan-name"
                placeholder="مثلاً: اشتراک سه‌ماهه"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="plan-desc">توضیحات</Label>
              <Textarea
                id="plan-desc"
                placeholder="توضیح کوتاه درباره طرح..."
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                disabled={submitting}
                className="min-h-16 text-sm"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="plan-price">مبلغ (تومان) *</Label>
                <Input
                  id="plan-price"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="150000"
                  value={form.priceToman}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      priceToman: e.target.value.replace(/[^\d]/g, ""),
                    })
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="plan-days">مدت (روز) *</Label>
                <Input
                  id="plan-days"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="30"
                  value={form.durationDays}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      durationDays: e.target.value.replace(/[^\d]/g, ""),
                    })
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="plan-traffic">حجم (گیگ) — 0 = نامحدود</Label>
                <Input
                  id="plan-traffic"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.trafficGB}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      trafficGB: e.target.value.replace(/[^\d]/g, ""),
                    })
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="plan-sort">ترتیب نمایش</Label>
                <Input
                  id="plan-sort"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      sortOrder: e.target.value.replace(/[^\d]/g, ""),
                    })
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-xl border bg-muted/40 p-3">
                <div>
                  <Label htmlFor="plan-popular" className="text-sm">
                    طرح محبوب
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    با نشان «محبوب‌ترین» نمایش داده می‌شود.
                  </p>
                </div>
                <Switch
                  id="plan-popular"
                  checked={form.popular}
                  onCheckedChange={(v) => setForm({ ...form, popular: v })}
                  disabled={submitting}
                />
              </div>
              <div className="flex items-center justify-between rounded-xl border bg-muted/40 p-3">
                <div>
                  <Label htmlFor="plan-active" className="text-sm">
                    فعال
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    طرح‌های غیرفعال در فروشگاه دیده نمی‌شوند.
                  </p>
                </div>
                <Switch
                  id="plan-active"
                  checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  disabled={submitting}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormOpen(false)}
                disabled={submitting}
              >
                انصراف
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال ذخیره...
                  </>
                ) : editing ? (
                  "ذخیره تغییرات"
                ) : (
                  "ایجاد طرح"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف طرح</AlertDialogTitle>
            <AlertDialogDescription>
              آیا از حذف طرح «{deleteTarget?.name}» مطمئن هستید؟ این عمل قابل بازگشت
              نیست.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>انصراف</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? "در حال حذف..." : "حذف"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
